import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../utils/api';
import LanguageSwitcher from '../components/LanguageSwitcher';
import QRForm from '../components/QRForm';

const EditQRPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const [fetching, setFetching] = useState(true);

  const fetchQRCode = useCallback(async () => {
    try {
      const response = await api.get(`/qr/${id}`);
      setInitialData(response.data);
    } catch (error) {
      toast.error(t('error_occurred'));
      navigate('/dashboard');
    } finally {
      setFetching(false);
    }
  }, [id, t, navigate]);

  useEffect(() => {
    fetchQRCode();
  }, [fetchQRCode]);

  const handleSubmit = async (formData) => {
    setLoading(true);
    try {
      await api.put(`/qr/${id}`, formData);
      toast.success(t('qr_updated'));
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_occurred'));
      throw error;
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-primary-50 to-white flex items-center justify-center">
        <p className="text-muted-foreground" data-testid="edit-loading">{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-primary-50 to-white">
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-white/20 shadow-sm">
        <div className="container mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" data-testid="back-to-dashboard-btn">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('back')}
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <Logo className="h-8 w-8" />
              <span className="text-2xl font-heading font-bold text-primary">cyQRus</span>
            </div>
          </div>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="container mx-auto px-6 md:px-12 py-8 max-w-4xl">
        <h1 className="text-3xl sm:text-4xl font-heading font-bold text-primary mb-8" data-testid="edit-qr-title">{t('edit')} QR Code</h1>
        <QRForm onSubmit={handleSubmit} loading={loading} initialData={initialData} isEdit={true} />
      </main>
    </div>
  );
};

export default EditQRPage;